import { ListItem, UnorderedList } from '@chakra-ui/react';
import { useChartDownload } from '@resistapp/client/contexts/chart-download-context';
import { useSearchParamsContext } from '@resistapp/client/contexts/search-params-context';
import { useUser } from '@resistapp/client/contexts/use-user-context';
import { AbunanceSelection } from '@resistapp/client/data-utils/filter-data/filter';
import { useNormalisationMode } from '@resistapp/client/hooks/use-query-filters/use-query-filters';
import { useIsModifyAsAdminActive } from '@resistapp/client/hooks/use-research-admin';
import {
  adminHeatmapAbundanceOptions,
  canUseResearchHeatmapGeneSetSelector,
  getEffectiveResearchHeatmapAbundanceMode,
  requiresResearchHeatmapEstimationsParam,
} from '@resistapp/client/utils/research-heatmap-abundance-mode';
import { getHeatmapNormalisationDescription, isConsideredAbsolute } from '@resistapp/common/normalisation-mode';
import { scaleOrdinal } from '@visx/scale';
import { BaseLegend } from './base-legend';
import {
  NOT_DETECTED_COLOR,
  getBinLabels,
  getHeatmapLegendColors,
  shouldRenderUnclearLegendTriangle,
} from './heatmap-bins';
import { Legend } from './legend';

interface HeatmapLegendProps {
  setAbundanceMode: (mode: AbunanceSelection) => void;
  abundanceMode: AbunanceSelection;
  legendHeight: string;
  id?: string;
}

export function HeatmapLegend({ abundanceMode, setAbundanceMode, legendHeight, id }: HeatmapLegendProps) {
  const normalisationMode = useNormalisationMode();
  const [isModifyAsAdminActive] = useIsModifyAsAdminActive();
  const { user } = useUser();
  const { setSearchParamsStable } = useSearchParamsContext();
  const canUseSelector = canUseResearchHeatmapGeneSetSelector(user, isModifyAsAdminActive);
  const effectiveAbundanceMode = getEffectiveResearchHeatmapAbundanceMode(abundanceMode, canUseSelector);
  const bins = getBinLabels(normalisationMode, effectiveAbundanceMode);
  const colors = getHeatmapLegendColors(normalisationMode, effectiveAbundanceMode);
  const colorScale = scaleOrdinal<string, string>({ domain: bins, range: colors });
  const labelFormat = (label: string | number) => String(label);
  const option = heatmapOptions.find(o => o.value === effectiveAbundanceMode) as Option;
  const numberExplanation = isConsideredAbsolute(normalisationMode)
    ? 'The colors in the heatmap show an estimation of gene copy numbers.'
    : 'The colors in the heatmap show gene abundances relative to 16S rRNA gene.';
  const { description, valueTip } = getHeatmapNormalisationDescription(normalisationMode);
  const { downloadChartAndLegend } = useChartDownload();
  const downloadTooltip = 'Download image of the Genes in detail chart or legend.';
  const selectorOptions = canUseSelector
    ? heatmapOptions.filter(o => adminHeatmapAbundanceOptions.includes(o.value))
    : undefined;
  const renderGlyph = ({ label, value, size }: { label: string; value: string; size: number }) =>
    shouldRenderUnclearLegendTriangle(normalisationMode, label) ? (
      <>
        <rect fill={NOT_DETECTED_COLOR} width={size} height={size} />
        <polygon points={`0,0 ${size},0 0,${size}`} fill={value} />
      </>
    ) : (
      <rect fill={value} width={size} height={size} />
    );
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '10px',
      }}
    >
      <BaseLegend
        id={id}
        header="Genes in detail"
        description={description}
        options={selectorOptions}
        onChange={value => {
          const mode = value as AbunanceSelection;
          setAbundanceMode(mode);
          setSearchParamsStable(prev => {
            const next = new URLSearchParams(prev.toString());
            if (requiresResearchHeatmapEstimationsParam(mode)) {
              next.set('estimations', 'true');
            } else {
              next.delete('estimations');
            }
            return next;
          });
        }}
        defaultValue={option.value}
        TooltipContent={
          <div>
            {valueTip ? <div style={{ marginBottom: '10px' }}>{valueTip}</div> : null}
            <UnorderedList>
              <ListItem>Heatmap colors correspond to ranges between logaritmic axis ticks on the box plot.</ListItem>
              <ListItem>{numberExplanation}</ListItem>
              {canUseSelector && <ListItem>Use this drop-down to change the displayed gene set.</ListItem>}
              <ListItem>You can limit the genes further by the above gene group selection.</ListItem>
            </UnorderedList>
          </div>
        }
        onDownload={
          id
            ? () => {
                downloadChartAndLegend({
                  legendId: id,
                  chartId: 'research-view-gene-details-chart',
                  plotType: 'heat',
                  label: 'Gene_details',
                });
              }
            : undefined
        }
        downloadTooltip={downloadTooltip}
      >
        <Legend
          colorScale={colorScale}
          labelFormat={labelFormat}
          getOpacity={() => 1}
          height={legendHeight}
          renderGlyph={renderGlyph}
        />
      </BaseLegend>
    </div>
  );
}

export const heatmapOptions: Option[] = [
  {
    label: 'All analysed genes',
    value: AbunanceSelection.ANALYSED,
  },
  {
    label: 'Detected and traces',
    value: AbunanceSelection.QUANTIFIED_AND_TRACES,
  },
  {
    label: 'Quantified only',
    value: AbunanceSelection.QUANTIFIED_ONLY,
  },
  {
    label: 'ARG2.1 + OneHealth',
    value: AbunanceSelection.MEASURED_OR_ESTIMATE,
  },
  {
    label: 'Estimates',
    value: AbunanceSelection.ESTIMATES,
  },
];

interface Option {
  label: string;
  value: AbunanceSelection;
}
